import { AccountSettingsDialog } from "@/components/ui/account-settings-dialog";
import { ProgressOverviewMenu } from "@/components/ui/progress-overview/progress-overview-menu";
import { useMenuLinks } from "@/components/ui/tool-header-bar";
import { ViewerHelpCenterMenu } from "@/components/ui/viewer-help-center-menu";
import { useAppSelector } from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { APP_VERSION } from "@/utils/env-constants";
import {
  FaroButton,
  FaroIconButton,
  FaroText,
  FaroTooltip,
  grey,
  NoTranslate,
  SphereXGIcon,
  useBreakpointMdUp,
  UserAvatar,
  UserAvatarIcon,
  UserMenu,
} from "@faro-lotv/flat-ui";
import { useAuthContext } from "@faro-lotv/gate-keepers";
import { selectDashboardUrl } from "@faro-lotv/project-source";
import ArrowBack from "@mui/icons-material/ArrowBack";
import { Box, Stack } from "@mui/material";
import { useRef, useState, type JSX } from "react";

// Both sides as wide on small screens, so the title is centered on the screen
const SIDE_MIN_WIDTH_SMALL = { xs: 120, md: 0 };

type WizardHeaderBarProps = {
  /** Name of the workflow */
  title: string;

  /** True to prevent leaving while an action is running */
  disabled: boolean;

  /** Called when the user wants to leave the workflow */
  onClose(): void;
};

/**
 * Like the header bar of the exclusive modes (ToolHeaderBar), with the logo next to the exit button.
 * The ToolHeaderBar of flat-ui supports neither, so its parts are composed here.
 *
 * @returns the header of a wizard mode
 */
export function WizardHeaderBar({
  title,
  disabled,
  onClose,
}: WizardHeaderBarProps): JSX.Element {
  const isMdUp = useBreakpointMdUp();

  return (
    <Box
      component="header"
      sx={{
        display: "grid",
        // The title stays centered and takes the room left by the two sides, wrapping if needed
        gridTemplateColumns:
          "minmax(max-content, 1fr) minmax(0, auto) minmax(max-content, 1fr)",
        alignItems: "center",
        minHeight: 56,
        backgroundColor: grey[0],
        borderBottom: `1px solid ${grey[200]}`,
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignSelf: "stretch",
          alignItems: "center",
          minWidth: SIDE_MIN_WIDTH_SMALL,
        }}
      >
        {/* Same exit button as the ToolHeaderBar, the label is dropped on small screens to leave room for the title */}
        <Stack
          sx={{ alignSelf: "stretch", borderRight: `1px solid ${grey[200]}` }}
        >
          <FaroButton
            aria-label="exit tool"
            variant="ghost"
            size="l"
            onClick={onClose}
            disabled={disabled}
            sx={{
              height: "100%",
              borderRadius: 0,
              px: { xs: 2, md: 3 },
              minWidth: { xs: 0, md: undefined },
            }}
          >
            <FaroText
              variant="heading16"
              display="inline-flex"
              alignItems="center"
              gap={1}
            >
              <ArrowBack />
              {isMdUp && "Exit"}
            </FaroText>
          </FaroButton>
        </Stack>
        <SphereXGIcon
          aria-label="Sphere XG"
          sx={{
            width: { xs: "2rem", md: "2.5rem" },
            height: { xs: "2rem", md: "2.5rem" },
            mx: { xs: 1.5, md: 3 },
          }}
        />
      </Stack>

      <FaroText
        variant={isMdUp ? "heading16" : "heading14"}
        sx={{
          px: 1,
          minWidth: 0,
          textAlign: "center",
          // Fully shown on small screens, on up to two lines
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {title}
      </FaroText>

      <Stack
        direction="row"
        sx={{
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1,
          pr: 1,
          minWidth: SIDE_MIN_WIDTH_SMALL,
        }}
      >
        <ProgressOverviewMenu />
        {isMdUp && <ViewerHelpCenterMenu />}
        <UserMenuButton />
      </Stack>
    </Box>
  );
}

/** @returns the avatar of the user, opening the user menu as in the ToolHeaderBar */
function UserMenuButton(): JSX.Element {
  const dashboardUrl = useAppSelector(selectDashboardUrl);
  const menuLinks = useMenuLinks(dashboardUrl);
  const currentUser = useAppSelector(selectCurrentUser);
  const { requestLogin, logout } = useAuthContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSettingPageOpen, setIsSettingPageOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <FaroTooltip
        title={
          currentUser?.email ? (
            <NoTranslate>{currentUser.email}</NoTranslate>
          ) : null
        }
      >
        <FaroIconButton
          ref={buttonRef}
          aria-label="user menu"
          onClick={() => setIsMenuOpen(true)}
          // Let the user avatar determine the size
          sx={{ [">:first-of-type"]: { width: "auto", height: "auto" } }}
        >
          <UserAvatar
            userDisplayInfo={currentUser}
            icon={currentUser ? undefined : <UserAvatarIcon />}
            shouldShowGradientBorder
            size="s"
          />
        </FaroIconButton>
      </FaroTooltip>
      {buttonRef.current && (
        <UserMenu
          anchorElement={buttonRef.current}
          open={isMenuOpen}
          closeMenu={() => setIsMenuOpen(false)}
          userDisplayInfo={currentUser}
          links={menuLinks}
          appVersion={APP_VERSION}
          onSettingsClick={() => setIsSettingPageOpen(true)}
          onLogoutClick={logout}
          onLoginClick={requestLogin}
        />
      )}
      {isSettingPageOpen && (
        <AccountSettingsDialog onClose={() => setIsSettingPageOpen(false)} />
      )}
    </>
  );
}
